/**
 * lazyWithRetry — drop-in replacement for React.lazy that recovers from
 * stale-chunk errors after a deploy.
 *
 * The bug: every Vite build hashes its chunk filenames based on content
 * (e.g. `StakeCallingsView-AbC123.js`). After a deploy, those hashes
 * change. A user with the *old* index.html still in browser memory will,
 * on next route navigation, fetch a chunk URL that no longer exists.
 * Firebase Hosting's catch-all SPA rewrite (firebase.json: source "**" →
 * /index.html) returns the HTML shell for that missing path — so the
 * browser tries to execute HTML as a JS module and throws:
 *
 *   "'text/html' is not a valid JavaScript MIME type"
 *
 * The cure is to reload, which fetches the fresh index.html with the
 * current chunk hashes. This wrapper does that automatically.
 *
 * Loop guard: a genuinely missing chunk (a real build bug, not a deploy)
 * would otherwise reload → fail → reload forever. So we record the time
 * of each auto-reload and refuse to reload again if the last one was
 * within RELOAD_WINDOW_MS. A real bug fails again instantly — inside the
 * window — so we fall through to the ErrorBoundary instead of looping;
 * a genuine new deploy happens minutes/hours later — outside the window —
 * so it still reloads silently. This is the key difference from a one-
 * shot "reload once per session" flag, which breaks on the *second*
 * deploy into the same tab — exactly what happens while iterating.
 *
 * Usage: replace `React.lazy(() => import('./X'))` with
 * `lazyWithRetry(() => import('./X'))`. No other call-site changes.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const SESSION_KEY = '__wardflow_chunk_reload__'
const RELOAD_WINDOW_MS = 10_000

/**
 * True if `err` looks like a failed dynamic-import / stale-chunk load —
 * the family of messages browsers throw when a hashed chunk URL from a
 * previous deploy no longer exists and Firebase serves index.html (HTML)
 * in its place.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    /Loading chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /text\/html.*is not a valid JavaScript MIME/i.test(msg)
  )
}

/**
 * Reload once to pick up the fresh index.html after a deploy — unless we
 * already reloaded within RELOAD_WINDOW_MS, in which case the chunk is
 * genuinely missing (a real build bug, or the user is offline) and
 * another reload would just loop. Returns true if a reload was triggered,
 * false if it was suppressed so the caller can surface the error instead.
 */
export function reloadForStaleChunk(): boolean {
  const lastReload = Number(sessionStorage.getItem(SESSION_KEY)) || 0
  if (Date.now() - lastReload < RELOAD_WINDOW_MS) return false
  sessionStorage.setItem(SESSION_KEY, String(Date.now()))
  window.location.reload()
  return true
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory()
    } catch (err) {
      if (!isChunkLoadError(err)) throw err

      // Stale chunk from a fresh deploy → reload to the fresh shell. If
      // the reload is suppressed (we just reloaded and it's STILL
      // failing), the chunk is genuinely missing — a real build bug or an
      // offline user — so re-throw and let the ErrorBoundary show it.
      if (!reloadForStaleChunk()) throw err

      // Reload in flight — return a never-resolving promise so React
      // renders nothing before the navigation takes effect.
      return new Promise<{ default: T }>(() => {
        // intentionally never resolves
      })
    }
  })
}
